import React, { useEffect, useState } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  DocumentTextIcon, 
  ArrowUpIcon,
  ArrowDownIcon,
  PlayIcon,
  HeartIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { datumKurz } from '../utils/datum';
import { getErrorMessage } from '../utils/errors';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { DashboardStats, Content, ContentType } from '../types';

/**
 * Was in einer Kachel steht, wenn niemand die Zahl gemessen hat.
 *
 * ⚠️ Bewusst KEINE 0. Eine 0 ist eine Behauptung — „null Benutzer", „null Aufrufe" —, und sie ist
 * von einer echten 0 nicht zu unterscheiden. Ein Strich behauptet nichts. Genau das war der Kern
 * des Befunds: der gescheiterte Abruf zeigte vier Kacheln mit `0` und sah damit aus wie ein leeres,
 * aber funktionierendes System.
 */
const OHNE_WERT = '—';

/**
 * Wie viele Kennzahl-Kacheln die Übersicht trägt — EINE Zahl für Lade-Zweig und Inhalt.
 *
 * ⚠️ Der Lade-Zweig zeichnet genau so viele Platzhalter, wie unten Kacheln stehen. Liefen die
 * beiden Zahlen auseinander, blitzten beim Laden zu viele Platzhalter auf und die Seite sprang
 * beim Umschalten auf den Inhalt. Als Kommentar stand diese Regel schon einmal hier und band
 * nichts; jetzt hängt der Lade-Zweig an dieser Konstante, und `Dashboard.test.tsx` zählt beide
 * Zweige nach (`.stat-card`). Wer eine Kachel hinzufügt oder entfernt, sieht den Test rot.
 */
const KACHEL_ANZAHL = 2;

/**
 * Raster der Kennzahl-Kacheln — dieselbe Klasse im Lade-Zweig wie im Inhalt.
 *
 * ⚠️ Gedeckelt wird die KACHEL, nicht die Seite. `md:grid-cols-2` hätte die zwei verbliebenen
 * Kacheln auf die halbe Seitenbreite aufgeblasen, und die Seite hat bewusst keine Begrenzung
 * (`.main-content` = `ml-64 flex-1`, Polster aus `.page-content`; „breit ist der Standard", siehe
 * `PageLayout`): das wären ~800 px bei 1920 px Fenster und ~1100 px bei 2560 px — für eine Zahl
 * links und ein 24-px-Symbol rechts. Mit `auto-fill` sind die Spuren höchstens 20 rem breit,
 * brechen von selbst um, und der Block bleibt so breit wie die Karten darunter. Die Klasse steht
 * als Konstante, damit die beiden Zweige nicht getrennt driften können.
 */
const KACHEL_RASTER = 'grid grid-cols-[repeat(auto-fill,minmax(16rem,20rem))] gap-6';

/** Zeile der Systemlage: Ampelfarbe, Schriftfarbe und Text — alle drei aus DEMSELBEN Wert. */
// Bewusst OHNE `PageLayout` (Schnitt F, 2026-08-12): Diese Seite hat keine Titelzeile mit
// Aktionsknopf — ihre Kopfzone IST das gebrandete Gradient-Banner mit Signet und Begrüßung. Die
// Vorlage würde eine zweite Überschrift darübersetzen. Rahmen und Rhythmus stimmen ohnehin schon
// überein (`space-y-6`, volle Breite, Polster aus `.page-content`); es gibt nichts einzusammeln.
const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentContent, setRecentContent] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  /**
   * Ob der Abruf gescheitert ist — eigener Zustand, NICHT aus der Meldung abgeleitet.
   *
   * ⚠️ `benachrichtigung?.type === 'error'` wäre die naheliegende Abkürzung und wäre falsch: der
   * Balken trägt einen „Schließen"-Knopf, und beim Schließen sprängen die Kacheln zurück auf die
   * erfundenen Nullen. Die Anzeige würde also genau in dem Moment wieder lügen, in dem die
   * Nutzerin die Meldung zur Kenntnis genommen hat.
   */
  const [abrufGescheitert, setAbrufGescheitert] = useState(false);
  /**
   * ⚠️ Diese Seite hatte als EINZIGE der Verwaltungsseiten gar keinen Melde-Baustein. `schwebend`
   * bleibt bewusst weg: die Eigenschaft rettet Seiten, deren Formular ein Overlay (`fixed inset-0
   * z-50`) ist und im Fehlerzweig offen bleibt — dahinter läge ein Balken im Textfluss unsichtbar.
   * Nachgemessen: das Dashboard hat keine Formular-Weiche, kein Modal, kein Overlay; es rendert
   * einen Lade-Zweig und den Inhalt. Im Fluss stehend ist und bleibt die Mehrheitsfassung.
   */
  const { inhalt: benachrichtigung, melde, schließe } = useBenachrichtigung();
  const { t } = useTranslation();

  useEffect(() => {
    // Prevent multiple fetches
    if (hasFetched) {
      console.log('⚠️  Dashboard data already fetched, skipping...');
      return;
    }

    const fetchDashboardData = async () => {
      if (hasFetched) return;
      
      try {
        setIsLoading(true);
        setHasFetched(true);
        // Ein neuer Versuch beginnt ohne Altlast. Heute läuft der Abruf genau einmal pro Einhängen
        // (`hasFetched`), die Zeile ist also ein Gleichstand-Aufruf ohne Wirkung — sie steht hier,
        // damit ein späterer „Erneut versuchen"-Knopf den Fehlerzustand nicht stehen lässt.
        setAbrufGescheitert(false);
        console.log('🔄 Fetching dashboard data from API...');

        const [statsData, contentData] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getContent({ page: 1, limit: 5 })
        ]);

        setStats(statsData);
        setRecentContent(contentData.data || []);
        console.log('✅ Dashboard data fetched from API');
      } catch (apiError) {
        console.error('❌ Dashboard API failed:', apiError);
        // ⚠️ Hier stand NUR die Konsolenzeile. Die Nutzerin sah davon nichts — und darunter zeigte
        // die Seite `stats?.totalUsers || 0` in vier Kacheln plus dreimal „Unknown" unter grüner
        // Ampel. Ein gescheiterter Abruf war von einem leeren, gesunden System nicht zu
        // unterscheiden. Deshalb ZWEI Dinge: die Meldung, und der Zustand, der die Zahlen
        // zurücknimmt.
        melde('error', `Übersicht konnte nicht geladen werden: ${getErrorMessage(apiError)}`);
        setAbrufGescheitert(true);
        setStats(null);
        setRecentContent([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    // `melde` fehlt bewusst in den Abhängigkeiten und dürfte auch drinstehen: der Haken gibt es aus
    // `useCallback([])` zurück, seine Identität wechselt nie. Die Ausnahme unten galt und gilt
    // `hasFetched`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    change?: number;
    /** Erklärt einen Wert, der keiner ist. Ein blanker Strich sagt nichts — auch keiner Vorlesehilfe. */
    hinweis?: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  }> = ({ title, value, change, hinweis, icon: Icon, color }) => (
    <div className="stat-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="stat-label">{title}</p>
          <p className="stat-value">{value}</p>
          {hinweis && <p className="stat-label mt-1">{hinweis}</p>}
          {change !== undefined && (
            <div className="flex items-center mt-1">
              {change >= 0 ? (
                <ArrowUpIcon className="w-4 h-4 text-success-500 mr-1" />
              ) : (
                <ArrowDownIcon className="w-4 h-4 text-error-500 mr-1" />
              )}
              <span className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
                {Math.abs(change)}%
              </span>
              <span className="text-secondary-500 text-sm ml-1">vs last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  /** Zahl oder Strich — nie eine erfundene 0. Begründung an `OHNE_WERT`. */
  const kachelWert = (wert: number | undefined): string | number =>
    abrufGescheitert ? OHNE_WERT : wert ?? 0;

  /** Steht unter jedem Strich, damit er nicht als Gestaltung durchgeht. */
  const kachelHinweis = abrufGescheitert ? 'Nicht abrufbar' : undefined;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Auch im Lade-Zweig — zwei Gründe. Erstens die Regel des Fehlerpfad-Wächters: hat eine
            Seite einen Balken, trägt ihn JEDER Rückgabezweig, sonst meldet die Seite in genau dem
            Zweig nicht, in dem der Fehler auftritt. Zweitens die Ansage-Ebene: der Baustein hält
            seine Live-Regionen dauerhaft im Baum (Begründung dort), und weil er hier wie unten das
            ERSTE Kind desselben `space-y-6`-Rahmens ist, bleiben sie über den Wechsel
            Laden→Inhalt hinweg dieselben Knoten. */}
        <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} />
        <div className={KACHEL_RASTER}>
          {[...Array(KACHEL_ANZAHL)].map((_, i) => (
            <div key={i} className="stat-card">
              <div className="skeleton h-20 w-full"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header">
              <div className="skeleton h-6 w-32"></div>
            </div>
            <div className="card-content">
              <div className="skeleton h-64 w-full"></div>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <div className="skeleton h-6 w-32"></div>
            </div>
            <div className="card-content">
              <div className="skeleton h-64 w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Benachrichtigung inhalt={benachrichtigung} onSchließen={schließe} />

      {/* Welcome Section */}
      <div className="gradient-blue-purple rounded-lg p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 flex items-center justify-center">
            <img 
              src="/images/EE_Signet_Gradient.png"
              alt="Energetic Eternity Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-2">{t('dashboard.welcome')}</h1>
            <p className="text-primary-100">
              {t('dashboard.overview')}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className={KACHEL_RASTER}>
        <StatCard
          title={t('dashboard.totalUsers')}
          value={kachelWert(stats?.totalUsers)}
          hinweis={kachelHinweis}
          icon={UsersIcon}
          color="bg-primary-500"
        />
        <StatCard
          title={t('dashboard.totalContent')}
          value={kachelWert(stats?.totalContent)}
          hinweis={kachelHinweis}
          icon={DocumentTextIcon}
          color="bg-accent-500"
        />
        {/* ⚠️ Hier standen die Kacheln „Aufrufe gesamt" und „Downloads gesamt" — beide dauerhaft
            0, nicht wegen fehlender Nutzung, sondern weil niemand zählt. Der Unterschied zwischen
            den beiden gehört ausgeschrieben, sonst zieht die nächste Leserin den falschen Schluss:
            „Aufrufe" kam aus `db.contentView.count()`; `ContentView` hat im ganzen Repo KEINE
            Schreibstelle, nur Lesezugriffe — dort kann nie eine Zeile entstehen. „Downloads" kam
            aus `db.download.count()`; `Download` HAT zwei Schreibstellen in `ContentService`
            (`downloadContent` und `downloadContentWithToken`), und die zweite hängt an einer Route
            OHNE Anmeldung (`GET /:id/download/:token` in `contentRoutes.ts`): ein anonymer
            Aufrufer kann die Tabelle sehr wohl füllen. Leer ist sie, weil kein Client sie ruft —
            nicht im CMS, nicht in der Web-App, nicht in der mobilen App, nicht in einem Skript —
            und weil es laut Erlösentscheidung nie Datei-Downloads geben wird, also auch nie einen
            Aufrufer. Bei `ContentView` ist die 0 Struktur, bei `Download` eine Entscheidung:
            „da kann nie etwas hineinlaufen" gilt nur für die erste.
            Entfernt statt auf einen Strich gesetzt — eine 0, die keine Messung ist, ist derselbe
            Fehler wie die erfundenen Nullen im Fehlerpfad (Begründung an `OHNE_WERT`), nur
            dauerhaft; ein Strich wiederum stünde für „nicht abrufbar", hier gibt es aber nichts zu
            rufen. Kommt echtes Zählen, ist das ein bewusster Bau mit Schreibstelle, Endpunkt und
            Anzeige. `totalRevenue` ist in denselben Verträgen derselbe Fall (`Subscription` hat
            null Schreibstellen, das CMS zeigt den Wert nirgends) — bleibt bewusst stehen, eigener
            Schnitt. */}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Content */}
        <div className="card">
          <div className="card-header">
            {/* ⚠️ Hier stand als Unterzeile dauerhaft `dashboard.noRecentContent` — „Keine neuen
                Inhalte", auch wenn direkt darunter welche standen. Eine Anzeige, die unabhaengig
                von den Daten etwas behauptet. Der Rumpf traegt den Leerfall ohnehin (siehe den
                letzten Zweig unten, derselbe Schluessel), die Kopfzeile war also nicht nur falsch,
                sondern auch doppelt. Ersatzlos raus statt durch einen neuen Text ersetzt: die
                Ueberschrift sagt bereits, was die Karte zeigt. */}
            <h3 className="card-title">{t('dashboard.recentContent')}</h3>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              {/* ⚠️ Dieselbe Falle wie auf der Inhalte-Seite (015ce37): ohne diesen Zweig zeigte
                  ein gescheiterter Abruf die einladende Leer-Ansicht — „Keine neuen Inhalte",
                  darunter „Inhalt erstellen". Der Fehlschlag forderte die Nutzerin also auf,
                  anzulegen, was längst da sein kann. */}
              {abrufGescheitert ? (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="w-12 h-12 text-error-300 mx-auto mb-4" />
                  <p className="text-secondary-500">Neueste Inhalte konnten nicht geladen werden.</p>
                  <p className="text-sm text-secondary-400">
                    Ob es welche gibt, ist damit offen — bitte die Seite neu laden.
                  </p>
                </div>
              ) : recentContent.length > 0 ? (
                recentContent.map((content) => (
                  <div key={content.id} className="flex items-center space-x-4 p-4 bg-secondary-50 rounded-lg">
                    <div className="flex-shrink-0">
                      {/* Verglichen wird gegen das Enum, nicht gegen Zeichenketten: die API
                          liefert die Prisma-Schreibweise ('AUDIO'), gegen 'audio' traf hier
                          nie etwas — es wurde schlicht kein Symbol gezeichnet. */}
                      {content.type === ContentType.AUDIO && (
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <PlayIcon className="w-5 h-5 text-primary-600" />
                        </div>
                      )}
                      {content.type === ContentType.VIDEO && (
                        <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                          <PlayIcon className="w-5 h-5 text-accent-600" />
                        </div>
                      )}
                      {content.type === ContentType.BLOG && (
                        <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                          <DocumentTextIcon className="w-5 h-5 text-success-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 truncate">
                        {content.title}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {content.type} • {datumKurz(content.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="badge badge-secondary">
                        {content.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
                  <p className="text-secondary-500">{t('dashboard.noRecentContent')}</p>
                  <p className="text-sm text-secondary-400">{t('content.createContent')}</p>
                </div>
              )}
            </div>
          </div>
          <div className="card-footer">
            <button className="btn btn-outline btn-sm">
              {t('content.title')}
            </button>
          </div>
        </div>

        {/* ⚠️ Hier stand die Kachel „System Health" mit drei Ampeln für Datenbank, Redis und
            Dateispeicher. Sie war reine Fiktion: `systemHealth` kommt im gesamten Backend NICHT
            vor (`grep` über energetic-eternity-backend/src: 0 Treffer), der Stats-Endpunkt liefert
            das Feld nie. Die Kachel zeigte deshalb dauerhaft dreimal „Unknown" — und leuchtete bis
            953f1bb sogar grün dazu, behauptete also ein gesundes System, das nie gemeldet wurde.
            Entfernt statt versteckt, wie Analytics und Einstellungen: entweder es funktioniert
            wirklich, oder es erscheint nicht. Wenn das Backend die Werte eines Tages liefert, ist
            das ein bewusster Bau mit echtem Verbraucher — nicht ein Feld, das seit Monaten
            niemand füllt. */}
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Quick Actions</h3>
          <p className="card-description">Common tasks to get you started</p>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="flex items-center space-x-3 p-4 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
              <DocumentTextIcon className="w-6 h-6 text-primary-600" />
              <span className="text-sm font-medium text-primary-900">Create Content</span>
            </button>
            <button className="flex items-center space-x-3 p-4 bg-accent-50 hover:bg-accent-100 rounded-lg transition-colors">
              <UsersIcon className="w-6 h-6 text-accent-600" />
              <span className="text-sm font-medium text-accent-900">Manage Users</span>
            </button>
            <button className="flex items-center space-x-3 p-4 bg-success-50 hover:bg-success-100 rounded-lg transition-colors">
              <ChartBarIcon className="w-6 h-6 text-success-600" />
              <span className="text-sm font-medium text-success-900">View Analytics</span>
            </button>
            <button className="flex items-center space-x-3 p-4 bg-warning-50 hover:bg-warning-100 rounded-lg transition-colors">
              <HeartIcon className="w-6 h-6 text-warning-600" />
              <span className="text-sm font-medium text-warning-900">Upload Media</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
